'use client';
import cn from 'classnames';
import { displayTierName } from '@/util/product_client_safe';
import Image from 'next/image';
import Text from '@/components/common/Text/Text';
import RichText from '@/components/common/RichText/RichText';
import Button from '@/components/common/Button/Button';

import { SubscriptionProduct } from '@/types/Subscription';
import { pluralize } from '@/util/string';

import Checkmark from 'public/img/icon/checkmark-pink.svg';

import './PlanCardCondensed.style.scss';

type PlanCardCondensedProps = {
  className?: string;
  planData: SubscriptionProduct;
  selectedPlanValue?: SubscriptionProduct;
  onPlanSelected?: (selectedPlanValue: SubscriptionProduct) => void;
  disabled?: boolean;
};

function PlanCardCondensed(props: PlanCardCondensedProps) {
  const {
    className = '',
    planData,
    selectedPlanValue,
    onPlanSelected,
    disabled = false
  } = props;

  const selected = selectedPlanValue === planData;

  return (
    <div
      className={cn('PlanCardCondensed cursor', className, {
        selected,
        disabled
      })}
      onClick={() => {
        if (!disabled) {
          onPlanSelected?.(planData);
        }
      }}
    >
      <div className='PlanCardCondensed-content'>
        <div className='PlanCardCondensed-title'>
          <div>
            {planData.name === 'Couture+' || planData.name === 'Classique+' ? (
              <Text
                variant='title-l'
                italic
              >
                {planData.name.slice(0, planData.name.length - 1)}
                <span className='plaintext'>+</span>
              </Text>
            ) : (
              <Text
                variant='title-l'
                italic
              >
                {displayTierName(planData.name)}
              </Text>
            )}
          </div>

          <div className='PlanCardCondensed-price'>
            <Text
              variant='title-l'
              italic
            >
              {/* Remove cents via .split() */}
              {planData.price.asFormattedString.split('.')[0]}
            </Text>
            <Text
              className='PlanCardCondensed-price-month'
              variant='text-s'
            >
              / month
              <br />
              {planData.price.savings && (
                <Text
                  variant='span-s'
                  className='PlanCardCondensed-savings'
                >
                  {planData.price.savings}
                </Text>
              )}
            </Text>
          </div>
        </div>

        <div className='PlanCardCondensed-closet-list-container'>
          <Text
            variant='title-xxs-500'
            uppercase
          >
            {planData.allowed_borrowed_items}{' '}
            {pluralize('item', planData.allowed_borrowed_items)} at a time
          </Text>

          <div className='PlanCardCondensed-closet-list'>
            {planData.closetAccess.map((closet, index) => {
              if (planData.name === 'Classique+' && index === 0) {
                return (
                  <Text
                    key={index}
                    className='PlanCard-closet-listItem'
                    variant='text-m'
                  >
                    2 from the {closet} closet
                  </Text>
                );
              } else if (planData.name === 'Classique+' && index === 1) {
                return null;
              }

              return (
                <Text
                  key={index}
                  className='PlanCardCondensed-closet-listItem'
                  variant='text-m'
                >
                  {(planData.name === 'Couture+' ||
                    planData.name === 'Classique+') &&
                    '1'}{' '}
                  from the {closet} closet
                  {planData.closetAccess[index + 1] && ' +'}
                </Text>
              );
            })}
          </div>
        </div>

        <div className='PlanCardCondensed-closet-access'>
          <RichText>{planData.descriptionHTML}</RichText>
        </div>

        <div className='PlanCardCondensed-items'>
          <div className='PlanCardCondensed-itemTypeList'>
            {planData.bulletPointCopy.map((bulletPoint, index) => {
              return (
                <div
                  className='PlanCardCondensed-itemTypeList-bullet'
                  key={index}
                >
                  <Image
                    className='PlanCardCondensed-itemTypeList-checkmark'
                    src={Checkmark}
                    width={18}
                    height={16}
                    alt='Checkmark icon'
                  />
                  <Text
                    className='PlanCardCondensed-itemTypeList-text'
                    variant='text-m'
                  >
                    {bulletPoint}
                  </Text>
                </div>
              );
            })}
          </div>
        </div>

        <div className='PlanCardCondensed-apply-container'>
          <Button
            className={cn('PlanCardCondensed-apply-button', {
              selected: selected
            })}
            onClick={() => {
              onPlanSelected?.(planData);
            }}
            disabled={selected || disabled}
          >
            {selected ? 'Selected' : 'Select'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PlanCardCondensed;
